import { Box, Container, IconButton } from '@chakra-ui/react';
import { ReactNode, useEffect, useState } from 'react';
import { FiChevronUp } from 'react-icons/fi';
import Navbar from '../Navbar';
import Footer from './Footer';
import { useAllPageElementConfigs } from '../../hooks/usePageElementConfig';
import SpartaNavbar from '../elements/SpartaNavbar';
import SponsorsSection from '../common/SponsorsSection';

interface MainLayoutProps {
  children: ReactNode;
  headerInsideContainer?: boolean;
  showSponsorsSection?: boolean;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children, headerInsideContainer = false, showSponsorsSection = true }) => {
  const [showTop, setShowTop] = useState(false);
  const { getStyles, getVariant, refreshKey } = useAllPageElementConfigs('homepage');
  const headerVariant = getVariant('header', 'unified');
  const sponsorsVariant = getVariant('sponsors', 'grid');
  const footerVariant = getVariant('footer', 'standard');
  const containerVariant = getVariant('container', 'boxed');
  const isFullWidthLayout = containerVariant === 'fullwidth';
  const isWideLayout = containerVariant === 'wide';
  const contentMaxW = isFullWidthLayout ? '100%' : isWideLayout ? '1400px' : 'container.xl';
  const headerIsInside = headerInsideContainer && headerVariant !== 'fullwidth';

  useEffect(() => {
    const onScroll = () => {
      try {
        setShowTop(window.scrollY > 400);
      } catch {}
    };
    window.addEventListener('scroll', onScroll, { passive: true } as any);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll as any);
  }, []);

  const scrollToTop = () => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }
  };

  return (
    <Box minH="100vh" bg="bg.app" overflowX="hidden">
      <Box id="top" position="absolute" top={0} left={0} />
      {headerIsInside ? (
        <>
          <Container maxW={contentMaxW} py={8} px={isFullWidthLayout ? 0 : undefined}>
            <Box key={`header-${refreshKey}-${headerVariant}`} as="header" data-element="header" data-variant={headerVariant} style={{ ...getStyles('header') }}>
              {headerVariant === 'sparta_navbar' ? (
                <SpartaNavbar />
              ) : (
                <Navbar fullWidth={headerVariant === 'fullwidth'} variant={headerVariant} />
              )}
            </Box>
            {children}
          </Container>
          {showSponsorsSection && (
            <Box key={`sponsors-${refreshKey}-${sponsorsVariant}`} data-element="sponsors" data-variant={sponsorsVariant} style={{ ...getStyles('sponsors') }}>
              <SponsorsSection />
            </Box>
          )}
          <Box key={`footer-${refreshKey}-${footerVariant}`} as="footer" data-element="footer" data-variant={footerVariant} style={{ ...getStyles('footer') }}>
            <Footer />
          </Box>
        </>
      ) : (
        <>
          <Box key={`header-${refreshKey}-${headerVariant}`} as="header" data-element="header" data-variant={headerVariant} style={{ ...getStyles('header') }}>
            {headerVariant === 'sparta_navbar' ? (
              <SpartaNavbar />
            ) : (
              <Navbar fullWidth={headerVariant === 'fullwidth'} variant={headerVariant} />
            )}
          </Box>
          <Container maxW={contentMaxW} py={8} px={isFullWidthLayout ? 0 : undefined}>
            {children}
          </Container>
          {/* Global sponsors section across front-facing pages */}
          {showSponsorsSection && (
            <Box key={`sponsors-${refreshKey}-${sponsorsVariant}`} data-element="sponsors" data-variant={sponsorsVariant} style={{ ...getStyles('sponsors') }}>
              <SponsorsSection />
            </Box>
          )}
          <Box key={`footer-${refreshKey}-${footerVariant}`} as="footer" data-element="footer" data-variant={footerVariant} style={{ ...getStyles('footer') }}>
            <Footer />
          </Box>
        </>
      )}
      {showTop && (
        <IconButton
          aria-label="Zpět nahoru"
          icon={<FiChevronUp />}
          position="fixed"
          right={{ base: 4, md: 6 }}
          bottom={{ base: 4, md: 6 }}
          zIndex={1000}
          colorScheme="blue"
          onClick={scrollToTop}
          borderRadius="full"
          shadow="md"
        />
      )}
    </Box>
  );
};

export default MainLayout;
